import { N, z } from "./dist-gYzkAC94.mjs";
import { fromEmpty } from "./fromEmpty-CKvfJSja.mjs";
import { z as z$1 } from "zod";

//#region src/z.ts
/**
* Custom error map for Zod that logs detailed error information to console
* and returns the default error message.
*
* @example
* ```ts twoslash
* import { customErrorMap, z } from '@simplisafe/ewok'
*
* const schema = z.string({ errorMap: customErrorMap })
* schema.parse(123) // Will log detailed error info and throw
* ```
*/
const customErrorMap = (issue, ctx) => {
	const { data } = ctx;
	const message = `
[Zod Error] - ${issue.code}: ${ctx.defaultError}
input: "${data}"
path: ${issue.path}
  `;
	console.error(message);
	return { message: ctx.defaultError };
};
/**
* You need to use this if you are using a z.union(),
* otherwise you will get console errors if a value doesn't match the first item in a union.
*
* @example
* ```ts twoslash
* import { z } from '@simplisafe/ewok'
*
* z.union([z.string({ errorMap: z.defaultErrorMap }), z.number()])
* ```
*/
const defaultErrorMap = (_issue, ctx) => {
	return { message: ctx.defaultError };
};
function makeOptional(schema) {
	return schema.transform(fromEmpty);
}
/**
* A wrapper for [Zod's z.array](https://zod.dev/?id=arrays) that automatically defaults to an empty array.
*
* @example
* ```ts twoslash
* import { z } from '@simplisafe/ewok'
*
* const schema = z.array(z.string())
* schema.parse(null) // => []
* schema.parse(undefined) // => []
* schema.parse([null]) // => ['']
* schema.parse(['one']) // => ['one']
* ```
*/
const array = (arr) => z$1.nullable(z$1.array(arr, { errorMap: customErrorMap })).transform((t) => t ?? []).default([]);
/**
* A wrapper for [Zod's z.array](https://zod.dev/?id=arrays) that is non empty.
* This is compatible with `fp-ts`'s `ReadonlyNonEmptyArray` and `NonEmptyArray` modules.
*
* @example
* ```ts twoslash
* import { z } from '@simplisafe/ewok'
*
* const schema = z.nonEmptyArray(z.string())
* schema.parse(['one']) // => ['one']
* ```
*/
const nonEmptyArray = (arr) => z$1.array(arr, { errorMap: customErrorMap }).nonempty();
/**
* A wrapper for [Zod's z.boolean](https://zod.dev/?id=booleans) that automatically defaults to false.
* You can pass in a custom default value or params to modify the schema.
*
* @example
* ```ts twoslash
* import { z } from '@simplisafe/ewok'
*
* const schema1 = z.boolean() // => defaults to false
* const schema2 = z.boolean(true) // => defaults to true
* const schema3 = z.boolean({ invalid_type_error: "This needs to be a boolean!"}) // => custom params with default to false
* const schema4 = z.boolean({ invalid_type_error: "This needs to be a boolean!"}).default(true) // => custom params with default to true
* ```
*/
const boolean = (defaultOrParams) => z(defaultOrParams).with(N.boolean, (bool) => z$1.nullable(z$1.boolean({ errorMap: customErrorMap })).transform((t) => t ?? bool).default(bool)).with(N.nullish, (__) => z$1.nullable(z$1.boolean({ errorMap: customErrorMap })).transform((t) => t ?? false).default(false)).otherwise((params) => z$1.nullable(z$1.boolean(params)).transform((t) => t ?? false).default(false));
/**
* A wrapper for [Zod's z.literal](https://zod.dev/?id=literals) that automatically defaults to to the value given.
*
* Never use a literal in a union, use `.or` instead or you will get console errors.
*
* @example
* ```ts twoslash
* import { z } from '@simplisafe/ewok'
*
* z.literal("foo").parse("foo") // => "foo"
* ```
*/
const literal = (x) => z$1.nullable(z$1.literal(x).default(x)).transform((t) => t ?? x);
/**
* A wrapper for [Zod's z.number](https://zod.dev/?id=numbers) that automatically defaults to 0.
* You can pass in a custom default value or params to modify the schema.
*
* @example
* ```ts twoslash
* import { z } from '@simplisafe/ewok'
*
* const schema1 = z.number() // => defaults to 0
* const schema2 = z.number(10) // => defaults to 10
* const schema3 = z.number({ invalid_type_error: "This needs to be a boolean!"}) // => custom params with default to 0
* const schema4 = z.number({ invalid_type_error: "This needs to be a boolean!"}).default(10) // => custom params with default to 10
* ```
*/
const number = (defaultOrParams) => z(defaultOrParams).with(N.number, (num) => z$1.nullable(z$1.number({ errorMap: customErrorMap })).transform((t) => t ?? num).default(num)).with(N.nullish, (__) => z$1.nullable(z$1.number({ errorMap: customErrorMap })).transform((t) => t ?? 0).default(0)).otherwise((params) => z$1.nullable(z$1.number(params)).transform((t) => t ?? 0).default(0));
/**
* A wrapper for [Zod's z.string](https://zod.dev/?id=strings) that automatically defaults to `''`.
* You can pass in a custom default value or params to modify the schema.
*
* If you do need to reject an empty string you can use `.refine`.
*
* @example
* ```ts twoslash
* import { z } from '@simplisafe/ewok'
*
* const schema1 = z.string() // => defaults to ""
* const schema2 = z.string("foo") // => defaults to "foo"
* const schema3 = z.string({ invalid_type_error: "This needs to be a boolean!"}) // => custom params with default to ""
* const schema4 = z.string({ invalid_type_error: "This needs to be a boolean!"}).default("foo") // => custom params with default to "foo"
* ```
*
* @example
* ```ts twoslash
* import { z } from '@simplisafe/ewok'
*
* z.string().parse(null) // => ""
* z.string().parse(undefined) // => ""
* z.string("foo").parse(null) // => "foo"
* z.string("foo").parse(undefined) // => "foo"
* z.string("foo").parse("bar") // => "bar"
* ```
*
* @example
* ```ts twoslash
* import { z } from '@simplisafe/ewok'
* // reject an empty string
* const npmEmptyString = z.string().refine(s => s !== "")
*
* ```
*/
const string = (defaultOrParams) => z(defaultOrParams).with(N.string, (str) => z$1.nullable(z$1.string({ errorMap: customErrorMap })).transform((t) => t ?? str).default(str)).with(N.nullish, (__) => z$1.nullable(z$1.string({ errorMap: customErrorMap })).transform((t) => t ?? "").default("")).otherwise((params) => z$1.nullable(z$1.string(params)).transform((t) => t ?? "").default(""));
/**
* Safely handle an object that might be null or undefined.
*
* Zod doesn't have a way to deep parse objects and assign default values for each key, so it's better to either have a null object fail,
* or use this to create an `Option<{}>`.
*
* @example
* ```ts twoslash
* import { z } from '@simplisafe/ewok'
*
* const schema = z.optionalObject({ foo: z.string() })
*
* schema.parse(null) // => None
* schema.parse(undefined) // => None
* schema.parse({}) // => Some({ foo: '' })
* schema.parse({ foo: 'bar' }) // => Some({ foo: 'bar' })
* ```
*/
function optionalObject(t) {
	return makeOptional(z$1.object(t).nullish());
}
/**
* Wrapper for Zod functions that are pre-configured to easily work with data that might be null/undefined without having to use a lot of extra boilerplate.
*
* Ideal for parsing Contentful's GraphQL data.
*
* @example So instead of having to do this:
* ```ts twoslash
* import z from 'zod'
*
* const schema = z.object({
*   contentful_id: z.nullable(z.literal('ContentfulEntry')).default('ContentfulEntry'),
*   title: z.nullable(z.string()).default(''),
*   skus: z.nullable(z.array(z.nullable(z.string()).default(''))).default([])
* }).default({})
* ```
*
* @example You can just write this:
* ```ts twoslash
* import { z } from '@simplisafe/ewok'
*
* const schema = z.object({
*   contentful_id: z.literal('ContentfulEntry'),
*   title: z.string(),
*   skus: z.array(z.string())
* })
* ```
*
* @module
* @category Zod
*/
const z$2 = {
	...z$1,
	array,
	boolean,
	defaultErrorMap,
	literal,
	makeOptional,
	nonEmptyArray,
	number,
	optionalObject,
	string
};

//#endregion
export { array, boolean, customErrorMap, defaultErrorMap, literal, makeOptional, nonEmptyArray, number, optionalObject, string, z$2 as z };
//# sourceMappingURL=z-DLZ9PZkV.mjs.map